import { useQuery } from '@tanstack/react-query';
import { sitesApi } from '@/services/api/sites';
import { turbinesApi } from '@/services/api/turbines';

/**
 * Hook for fetching fleet-wide statistics
 * Provides total counts for sites and turbines across the entire fleet
 */
export const useFleetStats = () => {
  const fleetTurbinesQuery = useQuery({
    queryKey: ['fleet-turbines'],
    queryFn: () => turbinesApi.getTurbines({ page: 1, size: 9999 }),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  });

  const totalSitesQuery = useQuery({
    queryKey: ['fleet-sites'],
    queryFn: () => sitesApi.getSites({ page: 1, size: 9999 }),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  });

  return {
    fleetTurbineCount: fleetTurbinesQuery.data?.total_count || 0,
    totalSiteCount: totalSitesQuery.data?.total_count || 0,

    // Combined state
    isLoading: fleetTurbinesQuery.isLoading || totalSitesQuery.isLoading,
    isFetching: fleetTurbinesQuery.isFetching || totalSitesQuery.isFetching,
    error: fleetTurbinesQuery.error || totalSitesQuery.error,

    // Individual query info
    turbinesQuery: {
      data: fleetTurbinesQuery.data,
      isLoading: fleetTurbinesQuery.isLoading,
      error: fleetTurbinesQuery.error,
    },
    sitesQuery: {
      data: totalSitesQuery.data,
      isLoading: totalSitesQuery.isLoading,
      error: totalSitesQuery.error,
    },
  };
};

/**
 * Hook for fetching site-specific statistics
 * Provides turbine count and details for a specific site
 */
export const useSiteStats = (siteId: string | null) => {
  const siteTurbinesQuery = useQuery({
    queryKey: ['site-turbines', siteId],
    queryFn: () =>
      turbinesApi.getTurbines({
        page: 1,
        size: 9999,
        site_id: siteId || undefined,
      }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const siteDetailsQuery = useQuery({
    queryKey: ['site-details', siteId],
    queryFn: () => sitesApi.getSites({ page: 1, size: 9999 }),
    enabled: !!siteId,
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    select: (data) => data.sites.find((site) => site.site_id === siteId) || null,
  });

  return {
    siteTurbineCount: siteTurbinesQuery.data?.total_count || 0,
    siteDetails: siteDetailsQuery.data,
    siteName: siteDetailsQuery.data?.site_name || null,

    // Combined state - should not be loading if no siteId is provided
    isLoading: siteId ? siteTurbinesQuery.isLoading || siteDetailsQuery.isLoading : false,
    isFetching: siteTurbinesQuery.isFetching || siteDetailsQuery.isFetching,
    error: siteTurbinesQuery.error || siteDetailsQuery.error,

    // Individual query info
    turbinesQuery: {
      data: siteTurbinesQuery.data,
      isLoading: siteTurbinesQuery.isLoading,
      error: siteTurbinesQuery.error,
    },
    siteQuery: {
      data: siteDetailsQuery.data,
      isLoading: siteDetailsQuery.isLoading,
      error: siteDetailsQuery.error,
    },
  };
};

/**
 * Combined hook that provides both fleet and site-specific statistics
 * Useful for pages that need both types of data (like Sites page)
 */
export const useFleetAndSiteStats = (selectedSiteId: string | null) => {
  const fleetStats = useFleetStats();
  const siteStats = useSiteStats(selectedSiteId);

  return {
    fleet: {
      turbineCount: fleetStats.fleetTurbineCount,
      siteCount: fleetStats.totalSiteCount,
      isLoading: fleetStats.isLoading,
      error: fleetStats.error,
    },
    selectedSite: {
      turbineCount: siteStats.siteTurbineCount,
      details: siteStats.siteDetails,
      name: siteStats.siteName,
      isLoading: siteStats.isLoading,
      error: siteStats.error,
    },
    isLoading: fleetStats.isLoading || siteStats.isLoading,
    error: fleetStats.error || siteStats.error,
  };
};
